import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Card from "@mui/material/Card";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { CHART_PALETTE, Legend, colorAt } from "@outpave/ds";
import type { LegendItem } from "@outpave/ds";
import { TimeFrameTitles } from "enums/AnalyticsTimeFrame";
import useSpendingData from "hooks/useSpendingData";
import type { PieItem, SpendingMonth } from "hooks/useSpendingData";
import { useIsSmallScreen } from "hooks/useIsSmallScreen";
import { formatCashFull } from "utils/formatCash";
import CardHeader from "components/common/CardHeader";
import CustomSkeleton from "components/common/CustomSkeleton";
import { Columns, Inline, Stack } from "components/layout/reusableLayouts";
import PieChartSpending from "./PieChartSpending";
import TopSpending, { PLOT_HEIGHT } from "./TopSpending";
import { useSpendingTabs } from "./SharedAnalyticUtils";
import type { AnalyticsFilters } from "./analyticsFilters";

interface SpendBreakdownPanelProps {
  filters: AnalyticsFilters;
}

const DEFAULT_GROUP = "userName";
const MUTED = "var(--color-chart-muted)";
/** Height a wrapped legend may take below the donut on a small screen. */
const STACKED_LEGEND_ROOM = 120;
/** A phone's column is too narrow for a donut as tall as the bars. */
const SMALL_DONUT = 220;

/**
 * One order for every group, largest share first, so a group keeps its colour
 * and its place in both charts. Groups only the monthly data names go last.
 */
export const spendOrder = (pie: PieItem[], keys: string[]): string[] => {
  const byShare = [...pie]
    .sort((a, b) => b.value - a.value)
    .map((item) => item.name);
  return [...byShare, ...keys.filter((key) => !byShare.includes(key))];
};

const monthTotal = (months: SpendingMonth[], key: string) =>
  months.reduce(
    (sum, month) =>
      sum + (typeof month[key] === "number" ? (month[key] as number) : 0),
    0,
  );

const SpendBreakdownPanel: React.FC<SpendBreakdownPanelProps> = ({
  filters,
}) => {
  const { t } = useTranslation();
  const tabs = useSpendingTabs();
  const isSmallScreen = useIsSmallScreen();
  // As tall as the monthly bars beside it, so the two charts share a height.
  const donutSize = isSmallScreen ? SMALL_DONUT : PLOT_HEIGHT;
  const [groupKey, setGroupKey] = useState(DEFAULT_GROUP);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const activeTab = tabs.find((tab) => tab.groupKey === groupKey) ?? tabs[0];

  const pie = useSpendingData({ type: "pie", groupKey, filters });
  const bar = useSpendingData({ type: "bar", groupKey, filters });
  const currency = pie.currency || bar.currency;
  const months = bar.barData.data;

  useEffect(() => {
    setSelectedIds([]);
    setHighlightedId(null);
  }, [pie.pieData, bar.barData]);

  const order = useMemo(
    () => spendOrder(pie.pieData, bar.barData.keys),
    [pie.pieData, bar.barData.keys],
  );
  const colors = useMemo(
    () =>
      Object.fromEntries(
        order.map((name, index) => [name, colorAt(CHART_PALETTE, index)]),
      ),
    [order],
  );
  // A name is data, so the key and namespace separators must not apply to it.
  const labels = useMemo(
    () =>
      Object.fromEntries(
        order.map((name) => [
          name,
          t(name, { nsSeparator: false, keySeparator: false }),
        ]),
      ),
    [order, t],
  );
  const shares = useMemo(
    () =>
      new Map(
        order.map((name) => [
          name,
          pie.pieData.find((item) => item.name === name)?.value ??
            monthTotal(months, name),
        ]),
      ),
    [order, pie.pieData, months],
  );

  const slices = order
    .filter((name) => pie.pieData.some((item) => item.name === name))
    .map((name) => ({
      id: name,
      label: labels[name],
      value: shares.get(name) ?? 0,
      color: colors[name],
    }));
  const barKeys = order.filter((name) => bar.barData.keys.includes(name));

  const toggle = (name: string) =>
    setSelectedIds((current) =>
      current.includes(name)
        ? current.filter((id) => id !== name)
        : [...current, name],
    );

  const legendItems: LegendItem[] = order.map((name) => ({
    label: labels[name],
    value: formatCashFull(shares.get(name) ?? 0, currency),
    color:
      selectedIds.length > 0 && !selectedIds.includes(name)
        ? MUTED
        : colors[name],
  }));

  const loading =
    (pie.loading && pie.pieData.length === 0) ||
    (bar.loading && months.length === 0);

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        padding: "var(--space-6)",
        gap: "var(--space-4)",
      }}
    >
      <CardHeader
        title={t(activeTab.title)}
        subtitle={t(TimeFrameTitles[filters.timeFrame])}
      />

      <Tabs
        value={activeTab.groupKey}
        onChange={(_event, next: string) => setGroupKey(next)}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs
          .filter((tab) => !tab.isInvisible)
          .map((tab) => (
            <Tab key={tab.groupKey} label={t(tab.label)} value={tab.groupKey} />
          ))}
      </Tabs>

      {loading ? (
        <Stack sx={{ height: PLOT_HEIGHT }}>
          <CustomSkeleton variant="graph" />
        </Stack>
      ) : (
        <Columns gap="var(--space-7)" count={{ xs: 1, lg: 2 }}>
          <Stack gap="var(--space-4)" sx={{ minWidth: 0 }}>
            <Typography
              variant="label"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {t("Share of spend")}
            </Typography>
            <Inline gap="var(--space-7)" align="center" justify="center" wrap>
              <PieChartSpending
                slices={slices}
                size={donutSize}
                currency={currency}
                selectedIds={selectedIds}
                onSelectionChange={setSelectedIds}
                highlightedId={highlightedId}
                onHighlightChange={setHighlightedId}
              />
              {order.length > 0 && (
                <Legend
                  items={legendItems}
                  direction={isSmallScreen ? "horizontal" : "vertical"}
                  percent
                  maxHeight={isSmallScreen ? STACKED_LEGEND_ROOM : donutSize}
                  style={{ minWidth: 0 }}
                  onItemClick={(index) => toggle(order[index])}
                  onItemHover={(index) =>
                    setHighlightedId(index === null ? null : order[index])
                  }
                  itemPressed={(index) => selectedIds.includes(order[index])}
                />
              )}
            </Inline>
          </Stack>

          <Stack gap="var(--space-4)" sx={{ minWidth: 0 }}>
            <Typography
              variant="label"
              sx={{ color: "var(--color-text-tertiary)" }}
            >
              {t("Spend by month")}
            </Typography>
            <TopSpending
              months={months}
              keys={barKeys}
              labels={labels}
              colors={colors}
              currency={currency}
              selectedIds={selectedIds}
              onSelectionChange={setSelectedIds}
              highlightedId={highlightedId}
              onHighlightChange={setHighlightedId}
            />
          </Stack>
        </Columns>
      )}
    </Card>
  );
};

export default SpendBreakdownPanel;
